import { EventType, isThumbnailableContentType } from '@atlas/types'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { RealtimeConnectionState, subscribeRealtime, useRealtimeConnectionState } from '@/realtime/realtime-dispatcher'
import { type FileDTO, fileService } from '@/services/file.service'

/** Root of the thumbnail key family. Exported so the realtime query bridge invalidates the whole family on reconnect. */
export const THUMBNAIL_KEY = 'thumbnail'

/** Query key for one file's resolved thumbnail URL. Exported so the realtime query bridge invalidates the same key. */
export const thumbnailKey = (fileId: string) => [THUMBNAIL_KEY, fileId]

/**
 * Whether the worker generates a thumbnail for this file, read from the list the
 * concern registry itself is built from.
 *
 * Not a prefix test. `image/*` + `video/*` looks equivalent and is not: it misses
 * the PDF containers (`application/pdf`, and `.ai` as `application/postscript`),
 * so those files were processed, had a thumbnail written, and were never asked
 * for — the query stayed disabled and the tile kept its type marker with no error
 * to notice.
 */
const isThumbnailable = isThumbnailableContentType

const POLL_MS = 4000
/** Stop polling after ~1 min so a file whose thumbnail never lands doesn't poll forever. */
const MAX_POLLS = 15

/**
 * How much of a presigned URL's own lifetime to use before replacing it. 0.8 of a
 * 10-minute URL refreshes at 8 minutes, leaving 2 minutes of slack for the
 * round trip and for an image the browser starts loading just before the swap.
 *
 * Derived from the server's `expiresIn` rather than a constant of our own so the
 * two cannot drift: shortening the signed lifetime shortens the refresh with it.
 */
const REFRESH_AT_FRACTION = 0.8

/** The soonest a refresh is scheduled, so an already-expired URL is replaced promptly but not in a tight loop. */
const MIN_REFRESH_MS = 1000

/**
 * A resolved thumbnail plus the instant its URL should be replaced. `refreshAt` is
 * null when the server sent no usable `expiresIn` — the URL is kept as-is rather
 * than refreshed against a made-up lifetime.
 */
interface ResolvedThumbnail {
	url: string
	refreshAt: number | null
}

/** `expiresIn` seconds → the instant to replace the URL, or null if it is unusable. */
function refreshInstant(expiresIn: number): number | null {
	if (!Number.isFinite(expiresIn) || expiresIn <= 0) return null
	return Date.now() + expiresIn * 1000 * REFRESH_AT_FRACTION
}

/**
 * ms until this URL is due for replacement — `null` while there is no URL yet,
 * `false` for a URL that carries no refresh schedule.
 */
function msUntilRefresh(data: unknown): number | false | null {
	const resolved = data as ResolvedThumbnail | null | undefined
	if (!resolved) return null
	if (resolved.refreshAt === null) return false
	return Math.max(MIN_REFRESH_MS, resolved.refreshAt - Date.now())
}

/** Query state as the refetch/stale callbacks receive it. */
interface ThumbnailQueryState {
	state: { data?: unknown; dataUpdateCount: number }
}

/**
 * The query for one file's thumbnail URL: how to fetch it, and when to fetch it
 * again. `isPushing` only affects the *waiting* half — a resolved URL is refreshed
 * on its own schedule either way.
 */
function thumbnailQuery(file: FileDTO, isPushing: boolean) {
	return {
		queryKey: thumbnailKey(file.fileId),
		queryFn: async (): Promise<ResolvedThumbnail | null> => {
			// The list often already carries a thumbnailKey (Library grid). Only
			// re-fetch metadata when it's still missing - a freshly uploaded file
			// whose thumbnail is still being generated - so we don't fire an extra
			// getFile per file on a full library page.
			let hasThumbnail = Boolean(file.thumbnailKey)
			if (!hasThumbnail) {
				const meta = await fileService.getFile(file.fileId)
				hasThumbnail = Boolean(meta.thumbnailKey)
			}
			if (!hasThumbnail) return null
			const { thumbnailUrl, expiresIn } = await fileService.getThumbnailUrl(file.fileId)
			return { url: thumbnailUrl, refreshAt: refreshInstant(expiresIn) }
		},
		enabled: isThumbnailable(file.contentType),
		refetchInterval: (query: ThumbnailQueryState) => {
			// A resolved URL is presigned and therefore perishable: it is replaced on
			// its own schedule, whichever way the wait was done. Without this a tab
			// left open past the signed lifetime shows broken images with no error and
			// nothing to retry — the wait had ended, so nothing was watching.
			const untilRefresh = msUntilRefresh(query.state.data)
			if (untilRefresh !== null) return untilRefresh
			if (isPushing) return false // the subscription does the waiting
			if (query.state.dataUpdateCount >= MAX_POLLS) return false // give up — marker stays
			return POLL_MS
		},
		// Fresh exactly as long as the URL is, so a refocus after that refetches and
		// one before it does not. Zero while still waiting, and for a URL with no
		// schedule — a refocus is then the only thing that can repair it.
		staleTime: (query: ThumbnailQueryState) => msUntilRefresh(query.state.data) || 0,
	}
}

/**
 * Resolve a presigned thumbnail URL per file, keyed by fileId. Thumbnails are
 * generated asynchronously after upload, so the URL is not available at render time
 * and has to be waited for. Two ways of waiting, chosen by connection state
 * (ADR-0020):
 *
 * - **Connected** — a subscription to the rendered files' processing events. No
 *   interval at all: the wait costs nothing, so there is no cap on how long a slow
 *   thumbnail may take.
 * - **Otherwise** — poll the file metadata (always 200) every 4s until
 *   `thumbnailKey` is set. Polling the metadata rather than the thumbnail-url
 *   endpoint, which 404s until ready, avoids a spurious 404 on the first attempt.
 *   Capped at `MAX_POLLS` because an unbounded poll costs a request every 4s
 *   forever; that cap is the only reason it exists, so the push path does not
 *   inherit it.
 *
 * The switch is automatic — losing the connection mid-wait resumes polling.
 *
 * Once a URL is resolved the wait ends, but the query does not: the URL is
 * presigned and is replaced before it expires (see {@link REFRESH_AT_FRACTION}),
 * so a tab left open longer than the signed lifetime keeps working.
 *
 * Non-thumbnailable types are skipped and stay on the type marker. Shared by the
 * upload staging table and the Library grid (accepts any `FileDTO`-shaped list).
 */
export function useThumbnails(files: FileDTO[]): Record<string, string | null> {
	const queryClient = useQueryClient()
	const connectionState = useRealtimeConnectionState()
	const isPushing = connectionState === RealtimeConnectionState.CONNECTED

	// A string, not an array: the caller rebuilds `files` on every render, so an
	// array dependency would re-register the subscription each time. Joining the
	// ids makes an unchanged set compare equal.
	const subscribedIdKey = files
		.filter((file) => isThumbnailable(file.contentType))
		.map((file) => file.fileId)
		.join(',')

	useEffect(() => {
		const pendingFileIds = subscribedIdKey ? subscribedIdKey.split(',') : []
		if (!isPushing || pendingFileIds.length === 0) return

		// Invalidate and let the query re-read; the payload is deliberately not
		// inspected. A *completed* event can carry `{ skipped: true }` with no
		// thumbnail for non-thumbnailable input, so only the server's answer to the
		// query says whether a URL exists. Failures are handled the same way — they
		// end the wait, and the user-facing error comes from the notification bridge.
		return subscribeRealtime(
			{
				type: [EventType.FILE_PROCESSING_COMPLETED, EventType.FILE_PROCESSING_FAILED],
				entityRef: { id: pendingFileIds },
			},
			(message) => {
				void queryClient.invalidateQueries({ queryKey: thumbnailKey(message.entityRef.id) })
			},
		)
	}, [isPushing, subscribedIdKey, queryClient])

	const results = useQueries({ queries: files.map((file) => thumbnailQuery(file, isPushing)) })

	const byFileId: Record<string, string | null> = {}
	files.forEach((file, i) => {
		byFileId[file.fileId] = (results[i]?.data as ResolvedThumbnail | null | undefined)?.url ?? null
	})
	return byFileId
}
